import { supabase } from './supabase';
import { EmployeeLeaveBalance } from '../types';

/**
 * Service to handle automatic year-end reset of leave balances
 * This service resets leave balances at the end of each year according to Philippine labor standards
 */
export class YearEndResetService {
  /**
   * Reset all employee leave balances for the new year
   * This should be called automatically on January 1st or manually by HR
   */
  async resetAllEmployeeBalances(newYear: number): Promise<{
    success: boolean;
    message: string;
    employeesProcessed: number;
    errors: string[];
  }> {
    const errors: string[] = [];
    let employeesProcessed = 0;

    try {
      // Get all active employees
      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('id, first_name, last_name, status')
        .eq('status', 'active');

      if (empError) {
        throw new Error(`Failed to fetch employees: ${empError.message}`);
      }

      if (!employees || employees.length === 0) {
        return {
          success: true,
          message: 'No active employees found to reset balances for',
          employeesProcessed: 0,
          errors: []
        };
      }

      // Process each employee
      for (const employee of employees) {
        try {
          await this.resetEmployeeBalance(employee.id, newYear);
          employeesProcessed++;
        } catch (error) {
          const errorMsg = `Failed to reset balance for ${employee.first_name} ${employee.last_name}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMsg);
          console.error(errorMsg);
        }
      }

      return {
        success: errors.length === 0,
        message: `Successfully reset leave balances for ${employeesProcessed} employees${errors.length > 0 ? ` with ${errors.length} errors` : ''}`,
        employeesProcessed,
        errors
      };

    } catch (error) {
      return {
        success: false,
        message: `Failed to reset employee balances: ${error instanceof Error ? error.message : 'Unknown error'}`,
        employeesProcessed,
        errors: [error instanceof Error ? error.message : 'Unknown error']
      };
    }
  }

  /**
   * Reset leave balance for a specific employee
   */
  private async resetEmployeeBalance(employeeId: string, year: number): Promise<EmployeeLeaveBalance> {
    // Check if balance already exists for the new year
    const { data: existingBalance, error: checkError } = await supabase
      .from('employee_leave_balances')
      .select('id')
      .eq('employee_id', employeeId)
      .eq('year', year)
      .single();

    if (existingBalance) {
      // Balance already exists, update it to reset values
      const { data, error } = await supabase
        .from('employee_leave_balances')
        .update({
          // Reset all used days to 0 for the new year
          vacation_days_used: 0,
          sick_days_used: 0,
          personal_days_used: 0,
          emergency_days_used: 0,
          maternity_days_used: 0,
          paternity_days_used: 0,
          unpaid_days_used: 0,
          // Set Philippine standard leave allocations
          vacation_days_total: 5,   // Service Incentive Leave (SIL) - minimum required by law
          sick_days_total: 15,      // Common company practice
          personal_days_total: 5,   // Additional company benefit
          emergency_days_total: 7,  // Solo Parent Leave equivalent
          maternity_days_total: 105, // RA 11210 - 105-Day Expanded Maternity Leave Law
          paternity_days_total: 7,  // RA 8187 - Paternity Leave Act of 1996
          updated_at: new Date().toISOString()
        })
        .eq('employee_id', employeeId)
        .eq('year', year)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update existing balance: ${error.message}`);
      }

      return data;
    } else {
      // Create new balance record for the new year
      const { data, error } = await supabase
        .from('employee_leave_balances')
        .insert({
          employee_id: employeeId,
          year: year,
          // Philippine standard leave allocations
          vacation_days_total: 5,   // Service Incentive Leave (SIL)
          vacation_days_used: 0,
          sick_days_total: 15,      // Sick Leave
          sick_days_used: 0,
          personal_days_total: 5,   // Personal Leave
          personal_days_used: 0,
          emergency_days_total: 7,  // Solo Parent Leave
          emergency_days_used: 0,
          maternity_days_total: 105, // Maternity Leave
          maternity_days_used: 0,
          paternity_days_total: 7,  // Paternity Leave
          paternity_days_used: 0,
          unpaid_days_used: 0
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to create new balance: ${error.message}`);
      }

      return data;
    }
  }

  /**
   * Check if year-end reset is needed
   * This can be called periodically to check if we've entered a new year
   */
  async checkAndPerformYearEndReset(): Promise<{
    resetPerformed: boolean;
    message: string;
    year?: number;
  }> {
    const currentYear = new Date().getFullYear();
    
    try {
      // Check if we already have balances for the current year
      const { data: currentYearBalances, error } = await supabase
        .from('employee_leave_balances')
        .select('id')
        .eq('year', currentYear)
        .limit(1);

      if (error) {
        throw new Error(`Failed to check current year balances: ${error.message}`);
      }

      // If no balances exist for current year, perform reset
      if (!currentYearBalances || currentYearBalances.length === 0) {
        const resetResult = await this.resetAllEmployeeBalances(currentYear);
        
        return {
          resetPerformed: true,
          message: `Year-end reset performed for ${currentYear}: ${resetResult.message}`,
          year: currentYear
        };
      }

      return {
        resetPerformed: false,
        message: `Leave balances for ${currentYear} already exist, no reset needed`
      };

    } catch (error) {
      return {
        resetPerformed: false,
        message: `Failed to check/perform year-end reset: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Manually trigger year-end reset for a specific year
   * This can be used by HR to manually reset balances
   */
  async manualYearEndReset(year: number): Promise<{
    success: boolean;
    message: string;
    employeesProcessed: number;
  }> {
    const result = await this.resetAllEmployeeBalances(year);
    
    // Log the reset action
    console.log(`Manual year-end reset performed for ${year}:`, result);
    
    return {
      success: result.success,
      message: result.message,
      employeesProcessed: result.employeesProcessed
    };
  }
}

// Export singleton instance
export const yearEndResetService = new YearEndResetService();